import { AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { Timestamp } from '@angular/fire/firestore';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import dayjs from 'dayjs';
import moment from 'moment';
import { Observable, of, Subject, takeUntil } from 'rxjs';
import { AppExceptionModel } from '../../../../models/app-exception.model';
import {
  GradeLesson,
  GradeLessonStatus,
} from '../../../../models/grade-lesson.school.model';
import { Grade } from '../../../../models/grade.school.model';
import { Group } from '../../../../models/group.model';
import { NotesType } from '../../../../models/lesson.school.model';
import { UserModel } from '../../../../models/user.model';
import { AuthService } from '../../../../services/auth.service';
import { SchoolGradeLessonService } from '../../../../services/grade-lesson.school.service';
import { UtilsService } from '../../../../services/utils.service';
import * as gradeLessonActions from '../../../../state/actions/grade-lesson.school.actions';
import { AppState } from '../../../../state/appstate';
import * as gradeSelector from '../../../../state/selectors/grade.school.selector';
import * as groupSelector from '../../../../state/selectors/group.selector';
import { BtnSubmitComponent } from '../../../components/buttons/btn-submit/btn-submit.component';

@Component({
  selector: 'app-dlg-add-to-grade',
  standalone: true,
  imports: [BtnSubmitComponent, AsyncPipe, ReactiveFormsModule],
  templateUrl: './dlg-add-to-grade.component.html',
  styleUrl: './dlg-add-to-grade.component.css',
})
export class DlgAddToGradeComponent {
  store: Store<AppState> = inject(Store<AppState>);
  utilsService = inject(UtilsService);
  authService = inject(AuthService);
  lessonService: SchoolGradeLessonService = inject(SchoolGradeLessonService);
  readonly dialogRef = inject(MatDialogRef<DlgAddToGradeComponent>);
  private dialog: MatDialog = inject(MatDialog);

  readonly data = inject<any>(MAT_DIALOG_DATA);
  pageForm: FormGroup;

  gradeId: string | null = null;
  group: Group | undefined;
  grades: Grade[] = [];
  user: UserModel | null | undefined = null;

  title: string = 'Add Lesson To Grade';
  description: string = 'Select grade to add lesson to';
  action: string = 'Add';

  loading$: Observable<boolean> = of(false);

  exit$: Subject<void> = new Subject<void>();

  minTime = dayjs().format('YYYY-MM-DDTHH:mm');

  constructor() {
    this.title = this.data.title;
    this.description = this.data.description;
    this.action = this.data.action;
    this.gradeId = this.data.lesson.gradeId;

    this.pageForm = new FormGroup({
      gradeId: new FormControl(this.data.lesson.gradeId, [Validators.required]),
      publishBy: new FormControl(moment().format('YYYY-MM-DDTHH:mm')),
    });
  }

  ngOnInit(): void {
    this.store
      .select((state) => state.user.user)
      .pipe(takeUntil(this.exit$))
      .subscribe((user) => {
        this.user = user;
      });

    this.store
      .select(groupSelector.getGroupById(this.data.groupId))
      .pipe(takeUntil(this.exit$))
      .subscribe((group) => {
        this.group = group;
      });

    this.store
      .select(gradeSelector.getGradesByGroup(this.data.groupId))
      .pipe(takeUntil(this.exit$))
      .subscribe((grades) => {
        this.grades = grades;
      });
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  private toTimestamp(value: any): Timestamp {
    if (!value) {
      return Timestamp.now();
    }
    if (value instanceof Timestamp) {
      return value;
    }
    if (typeof value.toDate === 'function') {
      return Timestamp.fromDate(value.toDate());
    }
    if (typeof value.seconds === 'number') {
      return new Timestamp(value.seconds, value.nanoseconds ?? 0);
    }
    if (typeof value._seconds === 'number') {
      return new Timestamp(value._seconds, value._nanoseconds ?? 0);
    }
    const m = moment(value);
    if (m.isValid()) {
      return Timestamp.fromDate(m.toDate());
    }
    return Timestamp.now();
  }

  async submit(): Promise<void> {
    if (
      this.pageForm.invalid ||
      this.group === undefined ||
      this.data.lesson === undefined
    ) {
      return;
    }
    this.loading$ = of(true);
    try {
      const createdBy =
        this.user?.name ||
        this.authService.getUserName() ||
        this.data.lesson?.createdBy ||
        '';
      const createdById =
        this.user?.uid ||
        this.authService.getUid() ||
        this.data.lesson?.createdById ||
        '';

      const gradeLesson: GradeLesson = {
        lessonName: this.data.lesson.lessonName,
        lessonId: this.data.lesson.id,
        groupId: this.data.groupId,
        gradeId: this.pageForm.value.gradeId,
        academicYear: Number(this.group?.academicYear),
        notesUrl: this.data.lesson.notesUrl,
        notesType: this.data.lesson.notesType ?? NotesType.text,
        lessonStatus: GradeLessonStatus.open,
        publishBy: Timestamp.fromDate(
          moment(this.pageForm.get('publishBy')?.value).toDate(),
        ),
        tags: this.data.tags ?? this.data.lesson?.tags ?? [],
        createdBy: createdBy,
        createdById: createdById,
        created: this.toTimestamp(this.data.lesson.created),
        modified: Timestamp.now(),
      } as GradeLesson;

      const lessonId: string = await this.lessonService.create(
        this.data.groupId,
        `${this.data.lesson.id}_${this.pageForm.value.gradeId}_${this.group?.academicYear}`,
        gradeLesson,
      );
      this.store.dispatch(
        gradeLessonActions.createSuccess({
          lesson: { ...gradeLesson, id: lessonId },
        }),
      );
      this.dialogRef.close(true);
    } catch (error) {
      this.utilsService.onShowAlert(
        { title: ' Error Occurred', message: error } as AppExceptionModel,
        false,
      );
    }
    this.loading$ = of(false);
  }

  onGradeChange(event: any) {
    this.gradeId = event.target.value;
  }
}
